/**
 * Streaming Renderer Constants
 *
 * Configuration values for token buffering, block detection, and rendering.
 */

// =============================================================================
// TOKEN BUFFERING CONFIGURATION
// =============================================================================

/**
 * Maximum delay between buffer flushes in milliseconds.
 *
 * 30ms ≈ 2 frames at 60fps - imperceptible to users.
 * Balances responsiveness with reducing DOM update frequency.
 * Lower values = more responsive but more CPU.
 * Higher values = batched updates but noticeable lag.
 */
export const RENDER_DELAY_MS = 30;

/**
 * Minimum buffer size before immediate flush.
 *
 * 5 characters is typically one word or a meaningful chunk.
 * Prevents per-character rendering (jarring typewriter effect)
 * while keeping content flowing naturally.
 */
export const MIN_BUFFER_SIZE = 5;

/**
 * Maximum content length in characters (50KB).
 *
 * Prevents memory issues from extremely long responses.
 * 50KB is generous for any realistic AI response while
 * providing protection against runaway streaming.
 */
export const MAX_CONTENT_LENGTH = 50000;

/**
 * How long to wait after last token before showing loading indicator (ms).
 */
export const LOADING_DELAY_MS = 150;


// =============================================================================
// BLOCK TYPE CONFIGURATION
// =============================================================================

/** Valid block types for JSON-based blocks */
export const VALID_BLOCK_TYPES = [
  'STATS', 'CARDS', 'CARD-GRID', 'LIST', 'TABLE', 'HERO',
  'COMPARISON', 'FEATURE', 'PROCESS', 'INTERACTIVE', 'TIMELINE',
  'FAQ', 'TESTIMONIAL', 'ALERT', 'GALLERY', 'FLOW-FORM', 'FLOW_FORM',
  'LIVE-SECTION', 'LIVE_SECTION', 'TEXT', 'DEMO',
  'VIDEO', 'EMBED', 'TEAM', 'LOGOS', 'TABS', 'MAP', 'PRICING', 'CODE',
  'BROWSE', 'CALENDAR',
  'MORTGAGE-CALC', 'LOAN-CALC', 'STAMP-DUTY', 'AFFORDABILITY-CALC',
  'BEFORE-AFTER', 'COUNTDOWN', 'QUIZ', 'RATING-SUMMARY'
] as const;

/** Block types that use colon syntax (no closing tag) */
export const COLON_SYNTAX_TYPES = [
  'LIVE-SECTION', 'LIVE_SECTION', 'FLOW-FORM', 'FLOW_FORM', 'DEMO'
] as const;

/** Human-readable names for block types (used in loading placeholders) */
export const BLOCK_TYPE_NAMES: Record<string, string> = {
  'STATS': 'statistics',
  'CARDS': 'cards',
  'LIST': 'list',
  'TABLE': 'table',
  'COMPARISON': 'comparison',
  'FEATURE': 'feature',
  'PROCESS': 'steps',
  'INTERACTIVE': 'interactive',
  'TIMELINE': 'timeline',
  'FAQ': 'FAQ',
  'TESTIMONIAL': 'testimonials',
  'ALERT': 'alert',
  'GALLERY': 'gallery',
};

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type ValidBlockType = typeof VALID_BLOCK_TYPES[number];
export type ColonSyntaxType = typeof COLON_SYNTAX_TYPES[number];
